/**
 * Font loading and management for the handwriting engine.
 * Supports bundled fonts, user-uploaded fonts, and system fallbacks.
 */

// Bundled open-source fonts available in public/fonts/
// Users should add their own .ttf files here
const BUNDLED_FONTS = [];

// Cache for loaded FontFace objects
const fontCache = new Map();

/**
 * Get list of available bundled font names.
 * Scans the font directory and returns filenames.
 * In production, this is a static list; in dev, fonts are in public/fonts/.
 *
 * @returns {Promise<string[]>} Array of font filenames
 */
export async function getBundledFonts() {
  if (BUNDLED_FONTS.length > 0) {
    return BUNDLED_FONTS;
  }

  // Static list of available fonts
  // Add font filenames here as you add .ttf files to public/fonts/
  const knownFonts = [
    // Open-source fonts can be listed here
  ];

  BUNDLED_FONTS.push(...knownFonts);
  return BUNDLED_FONTS;
}

/**
 * Load a font from a URL into the browser's font registry.
 *
 * @param {string} name - Font family name
 * @param {string} url - URL to the font file (.ttf, .otf, .woff2)
 * @returns {Promise<FontFace>}
 */
export async function loadFontFromUrl(name, url) {
  const cacheKey = `url:${name}:${url}`;
  if (fontCache.has(cacheKey)) {
    return fontCache.get(cacheKey);
  }

  try {
    const font = new FontFace(name, `url(${url})`);
    const loadedFont = await font.load();
    document.fonts.add(loadedFont);
    fontCache.set(cacheKey, loadedFont);
    return loadedFont;
  } catch (err) {
    console.error(`Failed to load font "${name}" from ${url}:`, err);
    throw err;
  }
}

/**
 * Load a user-uploaded font file (.ttf) into the browser.
 *
 * @param {File} file - Font file from file input
 * @returns {Promise<{name: string, font: FontFace}>}
 */
export async function loadUploadedFont(file) {
  const name = file.name.replace(/\.(ttf|otf|woff2?)$/i, '');
  const cacheKey = `upload:${name}`;

  // If already loaded, return cached
  if (fontCache.has(cacheKey)) {
    return { name, font: fontCache.get(cacheKey) };
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const font = new FontFace(name, arrayBuffer);
    const loadedFont = await font.load();
    document.fonts.add(loadedFont);

    fontCache.set(cacheKey, loadedFont);
    return { name, font: loadedFont };
  } catch (err) {
    console.error(`Failed to load uploaded font "${name}":`, err);
    throw err;
  }
}

/**
 * Wait for a font to be ready for use on a canvas.
 *
 * @param {string} family - Font family name
 * @returns {Promise<void>}
 */
export async function waitForFont(family) {
  try {
    await document.fonts.load(`16px "${family}"`);
  } catch (err) {
    console.warn(`Font "${family}" not ready, using fallback:`, err);
  }
}

/**
 * Clear the font cache.
 */
export function clearFontCache() {
  fontCache.clear();
}
